import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn } from '../../components/DataGrid/DataGrid';
import attendanceService, { AttendanceRecord } from '../../services/attendanceService';
import employeeService, { Employee } from '../../services/employeeService';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './HrListPage.css';
import { RefreshCw, LogIn, LogOut, CalendarCheck, Clock, Users } from 'lucide-react';

const MAX_NOTES_LENGTH = 300;

const todayIso = () => new Date().toISOString().slice(0, 10);

const AttendancePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [openRecord, setOpenRecord] = useState<AttendanceRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [punching, setPunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [filterEmployeeId, setFilterEmployeeId] = useState<number | ''>('');
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());

  const [punchEmployeeId, setPunchEmployeeId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');

  const canPunch = hasPermission(Permissions.AttendanceRecords.ClockInOut);

  const loadRecords = useCallback(async () => {
    setError(null);
    try {
      const { items } = await attendanceService.getAll({
        employeeId: filterEmployeeId === '' ? undefined : Number(filterEmployeeId),
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setRecords(items);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('attendance.failedToLoad'));
    }
  }, [filterEmployeeId, fromDate, toDate, t]);

  const loadEmployees = useCallback(async () => {
    try {
      const { items } = await employeeService.getAll();
      setEmployees(items.filter((employee) => employee.isActive));
    } catch (err) {
      setError(getServerErrorMessage(err) || t('attendance.failedToLoadEmployees'));
    }
  }, [t]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    setLoading(true);
    loadRecords().finally(() => setLoading(false));
  }, [loadRecords]);

  // Whoever is selected for punching decides whether we show Clock In or Clock Out.
  useEffect(() => {
    if (punchEmployeeId === '') {
      setOpenRecord(null);
      return;
    }
    let cancelled = false;
    attendanceService
      .getOpenRecord(Number(punchEmployeeId))
      .then((record) => {
        if (!cancelled) setOpenRecord(record);
      })
      .catch((err) => {
        if (!cancelled) setError(getServerErrorMessage(err) || t('attendance.failedToLoad'));
      });
    return () => {
      cancelled = true;
    };
  }, [punchEmployeeId, t]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadRecords();
    setRefreshing(false);
  };

  const handleClockIn = async () => {
    if (punchEmployeeId === '') {
      setError(t('attendance.selectEmployeeFirst'));
      return;
    }
    setError(null);
    setPunching(true);
    try {
      const record = await attendanceService.clockIn({
        employeeId: Number(punchEmployeeId),
        notes: notes || undefined,
      });
      setOpenRecord(record ?? null);
      setNotes('');
      setSuccessMessage(t('attendance.clockInSuccess'));
      await loadRecords();
    } catch (err) {
      setError(getServerErrorMessage(err) || t('attendance.failedToClockIn'));
    } finally {
      setPunching(false);
    }
  };

  const handleClockOut = async () => {
    if (!openRecord) return;
    setError(null);
    setPunching(true);
    try {
      await attendanceService.clockOut(openRecord.id, { notes: notes || undefined });
      setOpenRecord(null);
      setNotes('');
      setSuccessMessage(t('attendance.clockOutSuccess'));
      await loadRecords();
    } catch (err) {
      setError(getServerErrorMessage(err) || t('attendance.failedToClockOut'));
    } finally {
      setPunching(false);
    }
  };

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value));

  const formatTime = (value?: string | null) =>
    value ? new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' }).format(new Date(value)) : '—';

  /** Hours arrive as a TimeSpan string ("HH:mm:ss"); show them as "7h 30m". */
  const formatHours = (value?: string | null) => {
    if (!value) return '—';
    const [hours, minutes] = value.split(':');
    return t('attendance.hoursMinutes', { hours: Number(hours), minutes: Number(minutes) });
  };

  const columns: GridColumn[] = [
    {
      key: 'employeeName',
      label: t('attendance.employee'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'workDate',
      label: t('attendance.workDate'),
      sortable: true,
      width: '150px',
      render: (value) => formatDate(value as string),
    },
    {
      key: 'clockInAt',
      label: t('attendance.clockIn'),
      sortable: true,
      width: '120px',
      render: (value) => formatTime(value as string),
    },
    {
      key: 'clockOutAt',
      label: t('attendance.clockOut'),
      width: '120px',
      render: (value) => formatTime(value as string | null),
    },
    {
      key: 'hoursWorked',
      label: t('attendance.hoursWorked'),
      width: '130px',
      render: (value) => formatHours(value as string | null),
    },
    {
      key: 'notes',
      label: t('attendance.notes'),
      render: (value) => <span className="description-text">{value || '—'}</span>,
    },
  ];

  const openCount = records.filter((record) => !record.clockOutAt).length;

  return (
    <Layout>
      <div className="container hr-page">
        <div className="header">
          <h1 className="title">{t('attendance.title')}</h1>
          <div className="header-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-total"><CalendarCheck size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{records.length}</span>
              <span className="ims-stat-label">{t('attendance.recordsInRange')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-open"><Clock size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{openCount}</span>
              <span className="ims-stat-label">{t('attendance.stillClockedIn')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-active"><Users size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{employees.length}</span>
              <span className="ims-stat-label">{t('attendance.activeEmployees')}</span>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {canPunch && (
          <div className="ims-card hr-punch-card">
            <div className="ims-card-header">
              <span className="ims-card-title">{t('attendance.punchClock')}</span>
            </div>
            <div className="ims-card-body">
              <div className="hr-filter-bar">
                <div className="hr-filter-group">
                  <span className="hr-filter-label">{t('attendance.employee')}</span>
                  <select
                    className="form-select"
                    value={punchEmployeeId}
                    onChange={(e) => setPunchEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
                  >
                    <option value="">{t('attendance.selectEmployee')}</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.employeeCode} — {employee.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hr-filter-group hr-filter-group--grow">
                  <span className="hr-filter-label">{t('attendance.notes')}</span>
                  <input
                    type="text"
                    className="form-input"
                    value={notes}
                    maxLength={MAX_NOTES_LENGTH}
                    onChange={(e) => setNotes(e.target.value.substring(0, MAX_NOTES_LENGTH))}
                    placeholder={t('attendance.notesPlaceholder')}
                  />
                </div>

                <div className="hr-filter-group">
                  {openRecord ? (
                    <button
                      type="button"
                      className="ims-btn ims-btn-danger"
                      onClick={handleClockOut}
                      disabled={punching}
                    >
                      <LogOut size={15} strokeWidth={2} />
                      {t('attendance.clockOut')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ims-btn ims-btn-primary"
                      onClick={handleClockIn}
                      disabled={punching || punchEmployeeId === ''}
                    >
                      <LogIn size={15} strokeWidth={2} />
                      {t('attendance.clockIn')}
                    </button>
                  )}
                </div>
              </div>

              {openRecord && (
                <p className="hr-open-note">
                  {t('attendance.openSince', { time: formatTime(openRecord.clockInAt) })}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title">{t('attendance.filters')}</span>
          </div>
          <div className="ims-card-body">
            <div className="hr-filter-bar">
              <div className="hr-filter-group">
                <span className="hr-filter-label">{t('attendance.employee')}</span>
                <select
                  className="form-select"
                  value={filterEmployeeId}
                  onChange={(e) => setFilterEmployeeId(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <option value="">{t('attendance.allEmployees')}</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.employeeCode} — {employee.fullName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="hr-filter-group">
                <span className="hr-filter-label">{t('attendance.fromDate')}</span>
                <input
                  type="date"
                  className="form-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="hr-filter-group">
                <span className="hr-filter-label">{t('attendance.toDate')}</span>
                <input
                  type="date"
                  className="form-input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <DataGrid
          data={records}
          columns={columns}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('attendance.noRecordsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default AttendancePage;
